import React, { useEffect, useState } from "react";
import { Drawer, Progress, Spin, Empty, Button } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, ResponsiveContainer, Tooltip as ChartTooltip, YAxis } from "recharts";
import { getAccountReport } from "pages/Dashboard/Metrix_v2/function/api";
import type { AccountReport } from "pages/Dashboard/Metrix_v2/function/type";

export interface QuickViewAccount {
    accountId: string;
    platform?: string;
    nickname?: string;
    broker?: string;
    // Values already shown on the overview; used when the report has no trades yet and returns zeros
    balance?: number;
    equity?: number;
    initialBalance?: number;
}

interface AccountQuickViewProps {
    open: boolean;
    account: QuickViewAccount | null;
    onClose: () => void;
}

type ReportData = AccountReport["data"];

const toNum = (value: unknown): number => {
    const num = typeof value === "string" ? parseFloat(value.replace(/[^0-9.-]/g, "")) : Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatUsd = (value: unknown, withSign = false): string => {
    const num = toNum(value);
    const sign = withSign && num > 0 ? "+" : num < 0 ? "-" : "";
    return `${sign}$${Math.abs(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const pnlColor = (value: unknown) => {
    const num = toNum(value);
    if (num > 0) return "var(--stat-green-text)";
    if (num < 0) return "var(--stat-red-text)";
    return "var(--text-title)";
};

// Goal limits follow the FTMO-style rules used on the Metrix page: max loss 10%, daily loss 5%,
// profit target 10% (5% for verification). Backend-provided ftmo_progress wins when available.
const buildGoals = (data: ReportData, initial: number, balance: number, equity: number) => {
    const info = data.account_info;
    const status = String(info?.account_status || info?.type || "").toLowerCase();
    const progress = data.ftmo_progress;

    if (initial <= 0 && !progress) return null;

    const maxLossLimit = progress?.max_loss?.max_loss ?? initial * 0.1;
    const maxLossCurrent = progress?.max_loss?.current_loss ?? Math.max(0, initial - Math.min(balance, equity));
    const dailyLossLimit = progress?.daily_loss?.max_loss ?? initial * 0.05;
    const dailyLossCurrent = progress?.daily_loss?.current_loss ?? Math.max(0, -toNum(data.net_profit_today));
    const targetPct = status.includes("verification") ? 0.05 : 0.1;
    const targetProfit = progress?.profit_target?.target_profit ?? initial * targetPct;
    const currentProfit = progress?.profit_target?.current_profit ?? balance - initial;

    const pct = (current: number, limit: number) => (limit > 0 ? Math.min(100, Math.max(0, (Math.abs(current) / limit) * 100)) : 0);

    return [
        { key: "maxLoss", current: maxLossCurrent, limit: maxLossLimit, percent: pct(maxLossCurrent, maxLossLimit), isLoss: true },
        { key: "dailyLoss", current: dailyLossCurrent, limit: dailyLossLimit, percent: pct(dailyLossCurrent, dailyLossLimit), isLoss: true },
        { key: "profitTarget", current: currentProfit, limit: targetProfit, percent: currentProfit > 0 ? pct(currentProfit, targetProfit) : 0, isLoss: false },
    ];
};

const sectionStyle: React.CSSProperties = {
    background: "var(--card-bg)",
    border: "1px solid var(--card-border)",
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 12,
};

const sectionTitleStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-desc)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 10,
};

const AccountQuickView: React.FC<AccountQuickViewProps> = ({ open, account, onClose }) => {
    const { t } = useTranslation();
    const [report, setReport] = useState<ReportData | null>(null);
    const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!open || !account?.accountId) return;
        let cancelled = false;
        setStatus("loading");
        setReport(null);
        getAccountReport(account.accountId)
            .then((res) => {
                if (cancelled) return;
                if (res?.data) {
                    setReport(res.data);
                    setStatus("success");
                } else {
                    setStatus("error");
                }
            })
            .catch(() => {
                if (!cancelled) setStatus("error");
            });
        return () => {
            cancelled = true;
        };
    }, [open, account?.accountId, reloadKey]);

    const platform = (account?.platform || report?.account_info?.platform || "mt5").toLowerCase();
    const detailUrl = account ? `/${platform}/account-live-metrix/${account.accountId}` : "#";

    const renderBody = () => {
        if (status === "loading") {
            return (
                <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
                    <Spin />
                </div>
            );
        }
        if (status === "error" || !report) {
            return (
                <Empty description={t("accountQuickView.loadError")} style={{ padding: "60px 0" }}>
                    <Button onClick={() => setReloadKey((k) => k + 1)}>{t("accountQuickView.retry")}</Button>
                </Empty>
            );
        }

        const summary = report.performance_summary;
        const balance = toNum(summary?.current_balance) || toNum(report.account_info?.balance) || toNum(account?.balance);
        const equity = toNum(report.current_equity) || toNum(summary?.current_equity) || toNum(account?.equity) || balance;
        const initialBalance = toNum(report.account_info?.initial_balance) || toNum(account?.initialBalance);
        const netProfit = report.sum_net_profit ?? summary?.net_profit;
        const goals = buildGoals(report, initialBalance, balance, equity);
        const curve = (report.timeseries_by_trades || []).map((p) => ({ x: p.x, y: toNum(p.y) }));
        const recentTrades = [...(report.journal_closed_trades || [])]
            .sort((a, b) => new Date(b.close_time).getTime() - new Date(a.close_time).getTime())
            .slice(0, 5);
        // API returns win_rate as a ratio (0.6 = 60%); same normalisation as StatisticsOverview
        const rawWinRate = toNum(report.win_rate ?? summary?.win_rate);
        const winRate = rawWinRate > 0 && rawWinRate <= 1 ? rawWinRate * 100 : rawWinRate;
        const totalTrades = report.total_trades ?? summary?.total_trades ?? 0;

        const kpis = [
            { label: t("Balance"), value: formatUsd(balance), color: "var(--text-title)" },
            { label: t("Equity"), value: formatUsd(equity), color: "var(--text-title)" },
            { label: t("NetProfit"), value: formatUsd(netProfit, true), color: pnlColor(netProfit) },
            { label: t("Todayprofit"), value: formatUsd(report.net_profit_today, true), color: pnlColor(report.net_profit_today) },
        ];

        const stats = [
            { label: t("TotalTrades"), value: String(totalTrades) },
            { label: t("accountQuickView.winRate"), value: `${winRate.toFixed(1)}%` },
            { label: t("accountQuickView.profitFactor"), value: toNum(report.profit_factor).toFixed(2) },
            { label: t("accountQuickView.tradingDays"), value: String(report.count_trading_days ?? 0) },
            { label: t("accountQuickView.bestTrade"), value: summary?.best_trade ? formatUsd(summary.best_trade.profit, true) : "—" },
            { label: t("accountQuickView.worstTrade"), value: summary?.worst_trade ? formatUsd(summary.worst_trade.loss) : "—" },
        ];

        return (
            <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                    {kpis.map((kpi) => (
                        <div key={kpi.label} style={{ ...sectionStyle, marginBottom: 0, padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, color: "var(--text-desc)", fontWeight: 600, marginBottom: 4 }}>{kpi.label}</div>
                            <div className="om-num" style={{ fontSize: 16, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                        </div>
                    ))}
                </div>

                {curve.length > 1 && (
                    <div style={sectionStyle}>
                        <div style={sectionTitleStyle}>{t("accountQuickView.balanceCurve")}</div>
                        <div style={{ width: "100%", height: 120 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={curve} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                                    <defs>
                                        <linearGradient id="quickViewCurve" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#00C6B7" stopOpacity={0.35} />
                                            <stop offset="100%" stopColor="#00C6B7" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <YAxis hide domain={["dataMin", "dataMax"]} />
                                    <ChartTooltip
                                        formatter={(value: number) => formatUsd(value)}
                                        labelFormatter={(_, payload) => String(payload?.[0]?.payload?.x ?? "")}
                                    />
                                    <Area type="monotone" dataKey="y" stroke="#00C6B7" strokeWidth={2} fill="url(#quickViewCurve)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {goals && (
                    <div style={sectionStyle}>
                        <div style={sectionTitleStyle}>{t("accountQuickView.goals")}</div>
                        {goals.map((goal) => {
                            const danger = goal.isLoss && goal.percent >= 80;
                            const strokeColor = goal.isLoss ? (danger ? "#ef4444" : goal.percent >= 50 ? "#f59e0b" : "#22c55e") : "#00C6B7";
                            return (
                                <div key={goal.key} style={{ marginBottom: 10 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                                        <span style={{ fontWeight: 600, color: "var(--text-title)" }}>{t(`accountQuickView.${goal.key}`)}</span>
                                        <span className="om-num" style={{ color: "var(--text-desc)" }}>
                                            {formatUsd(goal.current)} / {formatUsd(goal.limit)}
                                        </span>
                                    </div>
                                    <Progress percent={Math.round(goal.percent)} strokeColor={strokeColor} size="small" />
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>{t("accountQuickView.statistics")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px 8px" }}>
                        {stats.map((stat) => (
                            <div key={stat.label}>
                                <div style={{ fontSize: 11, color: "var(--text-desc)" }}>{stat.label}</div>
                                <div className="om-num" style={{ fontSize: 14, fontWeight: 700, color: "var(--text-title)" }}>{stat.value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>{t("accountQuickView.recentTrades")}</div>
                    {recentTrades.length === 0 ? (
                        <div style={{ fontSize: 13, color: "var(--text-desc)" }}>{t("accountQuickView.noTrades")}</div>
                    ) : (
                        recentTrades.map((trade, idx) => (
                            <div
                                key={`${trade.close_time}-${idx}`}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 0",
                                    borderTop: idx === 0 ? "none" : "1px solid var(--bg-divider)",
                                    fontSize: 12.5,
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: 700, color: "var(--text-title)" }}>
                                        {trade.symbol}{" "}
                                        <span style={{ fontWeight: 600, color: String(trade.side).toLowerCase() === "buy" ? "var(--stat-green-text)" : "var(--stat-red-text)" }}>
                                            {String(trade.side).toUpperCase()}
                                        </span>{" "}
                                        <span style={{ fontWeight: 500, color: "var(--text-desc)" }}>{trade.volume} lot</span>
                                    </div>
                                    <div style={{ color: "var(--text-desc)", fontSize: 11 }}>{trade.close_time}</div>
                                </div>
                                <div className="om-num" style={{ fontWeight: 700, color: pnlColor(trade.net_profit) }}>
                                    {formatUsd(trade.net_profit, true)}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={Math.min(520, typeof window !== "undefined" ? window.innerWidth : 520)}
            destroyOnHidden
            title={
                <div>
                    <div style={{ fontWeight: 700 }}>
                        {account?.nickname || report?.account_info?.nickname || t("accountQuickView.title")}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-desc)" }}>
                        #{platform.toUpperCase()} · Login {account?.accountId}
                        {(account?.broker || report?.account_info?.broker) ? ` · ${account?.broker || report?.account_info?.broker}` : ""}
                        {report?.account_info?.account_status ? ` · ${report.account_info.account_status}` : ""}
                    </div>
                </div>
            }
            footer={
                <Link to={detailUrl} onClick={onClose}>
                    <Button type="primary" block style={{ background: "var(--primary-btn-bg)", color: "var(--primary-btn-text)", fontWeight: 700 }}>
                        {t("accountQuickView.openFull")}
                    </Button>
                </Link>
            }
        >
            {renderBody()}
        </Drawer>
    );
};

export default AccountQuickView;
